import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { useTranslation } from 'react-i18next';
import { trainingClient } from '../../../app/services/api/compliance/training';
import { TrainingCategoryDto } from '../../../app/services/api/compliance/training/trainingTypes';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import { UserContext } from '../../../components/shared/useUser';

const TrainingCategoriesPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation('training');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<TrainingCategoryDto[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TrainingCategoryDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<TrainingCategoryDto | null>(null);
  const [page, setPage] = useState(0);
  const { selectedFranchise } = useContext(UserContext);
  const [formData, setFormData] = useState<{
    tenantId: number;
    name: string;
    description: string;
    displayOrder: number;
    isActive: boolean;
  }>({
    tenantId: selectedFranchise?.tenantId || 0,
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true,
  });

  const rowsPerPage = 10;

  const maxPages = () => {
    return Math.ceil(categories.length / rowsPerPage);
  };

  const paginatedCategories = categories.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await trainingClient.getCategories({ includeInactive: true });
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingCategories.failedToLoadCategories'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: TrainingCategoryDto) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        tenantId: category.tenantId || selectedFranchise?.tenantId || 0,
        name: category.name,
        description: category.description || '',
        displayOrder: category.displayOrder,
        isActive: category.isActive,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        tenantId: selectedFranchise?.tenantId || 0,
        name: '',
        description: '',
        displayOrder: 0,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSave = async () => {
    const tenantId = selectedFranchise?.tenantId || formData.tenantId || 0;
    if (!tenantId) {
      setError(t('trainingCategories.franchiseRequired'));
      return;
    }

    try {
      setError(null);
      const payload = {
        tenantId,
        name: formData.name,
        description: formData.description,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };
      if (editingCategory) {
        await trainingClient.updateCategory(editingCategory.id, payload);
      } else {
        await trainingClient.createCategory(payload);
      }
      handleCloseDialog();
      setPage(0);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingCategories.failedToSaveCategory'));
    }
  };

  const handleDeleteClick = (category: TrainingCategoryDto) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      setError(null);
      await trainingClient.deleteCategory(categoryToDelete.id);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setPage(0);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingCategories.failedToDeleteCategory'));
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <PageTitle subTitle={t('trainingCategories.subtitle')}>{t('trainingCategories.title')}</PageTitle>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          alignItems={isMobile ? 'stretch' : 'center'}
        >
         
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={() => handleOpenDialog()}
          >
            {t('trainingCategories.newCategory')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Section>
        {categories.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text="categories"
            searchTerm={''}
            onClear={() => {}}
            onAdd={() => handleOpenDialog()}
            onAddIsCreating={false}
            addItemText={t('trainingCategories.newCategory')}
            onAddIsDisabled={false}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  Training Categories
                </Typography>
                <Pill text={`${categories.length} ${categories.length === 1 ? 'category' : 'categories'}`} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('trainingCategories.name')}</TableCell>
                    <TableCell>{t('trainingCategories.description')}</TableCell>
                    <TableCell align="center">{t('trainingModules.status')}</TableCell>
                    <TableCell align="right">{t('trainingModules.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCategories.map((category) => (
                    <TableRow
                      key={category.id}
                      hover
                      onClick={() => handleOpenDialog(category)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.custom.gray[50],
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip
                          title={category.name}
                          placement="top"
                        >
                          <Typography
                            variant="textSM"
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {category.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={category.description || 'No description'}
                          placement="top"
                        >
                          <Typography
                            variant="textSM"
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: category.description ? 'inherit' : theme.palette.custom.gray[500],
                            }}
                          >
                            {category.description || 'No description'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={category.isActive ? t('trainingCategories.active') : t('trainingCategories.inactive')}
                          color={category.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(category);
                          }}
                          title={t('trainingCategories.edit')}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(category);
                          }}
                          color="error"
                          title={t('trainingCategories.delete')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? t('trainingCategories.editCategory') : t('trainingCategories.createCategory')}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            pt={1}
          >
            <TextField
              label={t('trainingCategories.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('trainingCategories.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t('trainingCategories.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="primary"
            disabled={!formData.name.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        nameOfObject={categoryToDelete?.name || 'category'}
      />
    </PageArea>
  );
};

export default TrainingCategoriesPage;
